import { AgGridColumnProps, DataGrid, formatDate, SelectFilterComponent, SelectFloatingFilterComponent } from '@roc/ui';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@material-ui/core';
import { Edit, History, Info, MoreVert } from '@material-ui/icons';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { CreditPullTier, creditPullTierLabel } from '../../types/creditPullTiersTypes';
import { useCreditPullTiersSettingsStore } from '../../hooks/useCreditPullTiersStore';
import { TierAssignmentDialog } from './tierAssignmentDialog';
import { format } from 'date-fns';

const tierOptions = Object.values(CreditPullTier).map(tier => ({
  label: creditPullTierLabel[tier],
  value: tier,
}));

const TierColumnHeader = observer((params: any) => {
  const { creditPullTiersStore } = useCreditPullTiersSettingsStore();
  const { tiers } = creditPullTiersStore;

  const tooltipContent = (
    <div>
      {tiers.map(t => (
        <div key={t.tier}>{creditPullTierLabel[t.tier]}: {t.includedPulls} pulls/mo included</div>
      ))}
    </div>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <span>{params.displayName}</span>
      <Tooltip title={tooltipContent} arrow>
        <Info fontSize="small" style={{ marginLeft: 4, cursor: 'pointer', color: '#5c94de' }} />
      </Tooltip>
    </div>
  );
});

const today = format(new Date(), 'MM/dd/yyyy');

const columns: AgGridColumnProps[] = [
  {
    field: 'lenderName',
    headerName: 'TPO Name',
    minWidth: 200,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
    tooltipField: 'lenderId',
  },
  {
    field: 'creditPullTier',
    headerName: 'Current Tier',
    headerComponent: 'tierHeaderComponent',
    minWidth: 150,
    valueFormatter: ({ value }) => creditPullTierLabel[value] ?? value,
    filter: 'selectFilter',
    floatingFilter: true,
    floatingFilterComponent: 'selectFloatingFilter',
    floatingFilterComponentParams: { suppressFilterButton: true, options: tierOptions },
  },
  {
    field: 'creditPullsCount',
    headerName: `Credit Pulls/mo as of ${today}`,
    minWidth: 150,
  },
  {
    field: 'fundedLoansCount',
    headerName: `Funded Loans/mo as of ${today}`,
    minWidth: 150,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    minWidth: 100,
    width: 100,
    pinned: 'right',
    lockPinned: true,
    cellRenderer: 'actionCellRenderer',
  },
];

export const TierAssignmentGrid = observer(() => {
  const { creditPullTiersStore } = useCreditPullTiersSettingsStore();
  const gridStore = creditPullTiersStore.tierAssignmentGridStore;

  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const [record, setRecord] = useState<any>({});

  useEffect(() => {
    creditPullTiersStore.fetchAllTiers();
    gridStore.fetchGridData();
  }, [gridStore]);

  const openMenu = (event: React.MouseEvent<HTMLElement>, data: any) => {
    setRecord(data);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const onSortChanged = (params: any) => gridStore.setSortModel(params.api.getSortModel());
  const onFilterChanged = (params: any) => gridStore.setFilterModel(params.api.getFilterModel());

  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    tierHeaderComponent: TierColumnHeader,
    actionCellRenderer: (params: any) => (
      <IconButton onClick={e => openMenu(e, params.node.data)}>
        <MoreVert color="primary" />
      </IconButton>
    ),
  };

  return (
    <>
      <DataGrid
        columns={columns}
        rows={gridStore.gridData.data?.rows ?? []}
        isLoading={gridStore.isLoading}
        showFilters={gridStore.showFilters}
        sortModel={gridStore.sortModel}
        filterModel={gridStore.filterModel}
        paginationData={gridStore.gridData.meta}
        setPageNumber={gridStore.setPageNumber}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
        frameworkComponents={frameworkComponents}
      />
      <Menu
        id="menu"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          creditPullTiersStore.tierAssignmentStore.openDialog(record.lenderId);
        }}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><History fontSize="small" /></ListItemIcon>
          <ListItemText>Credit Pull History</ListItemText>
        </MenuItem>
      </Menu>
      <TierAssignmentDialog />
    </>
  );
});
